/**
 * /pilot/success — post-checkout landing page after Lemon Squeezy redirects back.
 * The LS checkout appends the `order_id` query-param; we simply thank the user.
 * The actual pilot activation is handled by the `order_created` webhook.
 */
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, Mail } from 'lucide-react';
import Logo from '@/components/Logo';

export default function PilotSuccess() {
  const [params] = useSearchParams();
  const pilotId = params.get('pilot_id') || '';
  const orderId = params.get('order_id') || '';

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pilot-success-page">
      <header className="border-b border-border bg-card/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/"><Logo size={30} variant="full" /></Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-xl w-full p-10" data-testid="pilot-success-card">
          <div className="w-14 h-14 rounded-2xl bg-green-50 border border-green-200 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2">Payment received — welcome aboard</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Your CBAM Readiness Pilot is now officially active. A Veridex specialist will reach out
            within <strong>48 hours</strong> with the kick-off invite and your secure data-intake folder.
          </p>

          <div className="rounded-lg border border-border bg-muted/30 p-4 mb-6 text-xs space-y-1">
            <div className="flex items-start gap-2"><Mail className="w-3.5 h-3.5 mt-0.5 text-muted-foreground" />
              A confirmation email has been sent to your executive sponsor.
            </div>
            {orderId && (
              <div className="font-mono text-muted-foreground" data-testid="pilot-success-order-id">
                Order reference: {orderId}
              </div>
            )}
            {pilotId && (
              <div className="font-mono text-muted-foreground" data-testid="pilot-success-pilot-id">
                Pilot ID: {pilotId}
              </div>
            )}
          </div>

          <Link to="/" data-testid="pilot-success-continue-btn">
            <Button className="bg-primary hover:bg-primary/90 w-full">
              Continue to Veridex <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>

          <p className="mt-6 text-xs text-muted-foreground text-center">
            Questions? Email <a href="mailto:pilot@veridex-one.com" className="text-primary hover:underline">pilot@veridex-one.com</a>
          </p>
        </Card>
      </main>
    </div>
  );
}
